'use client';

import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { MouseEvent, ReactNode } from 'react';
import { composeSx } from '../../utils/styledUtils';

export interface PhotoTileProps {
  /** Image src. If absent, the empty-state placeholder renders. */
  src?: string;
  /** Image alt text (or label when src is absent). */
  alt: string;
  /** Optional label rendered as an overlay caption at the bottom-left. */
  caption?: ReactNode;
  /** Optional ratio for the tile (width/height). Default 1 (square). */
  aspectRatio?: number;
  /** Click handler — makes the tile keyboard-focusable. */
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  /** Sx overrides. */
  sx?: SxProps<Theme>;
}

export function PhotoTile({ src, alt, caption, aspectRatio = 1, onClick, sx }: PhotoTileProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const interactive = Boolean(onClick);

  const placeholderBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
  const placeholderBorder = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
  const placeholderFg = theme.palette.text.disabled;

  return (
    <Box
      component={interactive ? 'button' : 'div'}
      {...(interactive ? { type: 'button' as const, onClick } : {})}
      aria-label={alt}
      sx={composeSx(
        {
          position: 'relative',
          aspectRatio: aspectRatio,
          width: '100%',
          overflow: 'hidden',
          borderRadius: 1,
          border: src ? 'none' : `1px dashed ${placeholderBorder}`,
          backgroundColor: src ? 'transparent' : placeholderBg,
          cursor: interactive ? 'pointer' : 'default',
          padding: 0,
          fontFamily: 'inherit',
          transition: 'transform 160ms ease, box-shadow 160ms ease',
          ...(interactive && {
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: `0 4px 12px ${alpha('#000', isDark ? 0.4 : 0.12)}`,
            },
            '&:focus-visible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }),
        },
        sx,
      )}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt={alt}
          loading="lazy"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      ) : (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            color: placeholderFg,
          }}
        >
          <HomeOutlinedIcon sx={{ fontSize: 32, opacity: 0.5 }} />
          <Typography
            variant="caption"
            sx={{
              color: placeholderFg,
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {alt}
          </Typography>
        </Box>
      )}
      {caption && src && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            px: 1,
            py: 0.75,
            background: `linear-gradient(to top, ${alpha('#000', 0.6)} 0%, transparent 100%)`,
            color: '#fff',
          }}
        >
          <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500 }}>
            {caption}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

PhotoTile.displayName = 'PhotoTile';
